import { daysDiff } from '@/helpers/date/dateHelpers';
import { toIsoDateString } from '@/helpers/date/dateValue';
import { startOfDay, subDays } from 'date-fns';

export const START_OF_ANALYTICS = '2025-01-01';
const START_OF_ANALYTICS_FOR_TRANSACTIONS = '2023-01-01';
const RESOURCES_WITH_END_DATE_AS_TODAY = ['transaction', 'test-transaction', 'bag'];
const RESOURCES_REQUIRING_ANALYTICS_PERIOD_TYPE_NULLISH = [
    'product',
    'product/summary',
    'device',
    'device/summary',
    'collection-point',
    'collection-point/summary',
    'device-group',
];

const PERIOD_TYPES = {
    DEFAULT: 1,
    WEEKLY: 7,
    MONTHLY: 30,
};

const PERIOD_CONFIGS = {
    7: { days: 7, type: PERIOD_TYPES.DEFAULT },
    14: { days: 14, type: PERIOD_TYPES.DEFAULT },
    30: { days: 30, type: PERIOD_TYPES.DEFAULT },
    90: { months: 3, type: PERIOD_TYPES.WEEKLY },
    180: { months: 6, type: PERIOD_TYPES.MONTHLY },
    360: { months: 12, type: PERIOD_TYPES.MONTHLY },
    0: { start: START_OF_ANALYTICS, type: PERIOD_TYPES.MONTHLY },
};

export const DEFAULT_PERIOD = 30;

const getResourceBaseName = resource => resource?.split?.('/')?.[0] ?? resource;

export const isPeriodEndDateToday = resource => RESOURCES_WITH_END_DATE_AS_TODAY.includes(getResourceBaseName(resource));

export const getPeriodMaxDate = resource => (isPeriodEndDateToday(resource) ? 'today' : 'yesterday');

export const getCustomPeriodDefaults = resource => {
    const today = startOfDay(new Date());
    const yesterday = startOfDay(subDays(today, 1));
    const endDate = isPeriodEndDateToday(resource) ? today : yesterday;

    return {
        periodStart_gte: toIsoDateString(yesterday),
        periodEnd_lte: toIsoDateString(endDate),
    };
};

export const calcPeriodType = type => {
    if (!type || 'custom' === type) {
        return PERIOD_TYPES.DEFAULT;
    }

    return PERIOD_CONFIGS[type].type;
};

const getYesterday = () => {
    let result = new Date();
    result.setDate(result.getDate() - 1);

    return result;
};

export const calcDays = filters => {
    if (filters.period && 'custom' !== filters.period) {
        const { startDate, endDate } = calcDateRange(filters.period);

        return daysDiff(endDate, startDate);
    }

    return daysDiff(new Date(filters.periodEnd_lte), new Date(filters.periodStart_gte));
};

export const calcDaysFromSeriesData = series => {
    if (!series || series.length === 0) {
        return 1;
    }

    const startDate = dateWithoutTime(new Date(series[0].periodStart.date));
    const endDate = dateWithoutTime(new Date(series[series.length - 1].periodEnd.date));

    return daysDiff(endDate, startDate);
};

export const transformPeriodFilters = (filters, resource) => {
    if (!filters) return;

    const transformedFilters = { ...filters };

    if (filters.period) {
        if ('custom' !== filters.period) {
            const { startDate, endDate } = calcDateRange(filters.period, resource);
            transformedFilters.periodStart_gte = startDate.toISOString().split('T')[0];
            transformedFilters.periodEnd_lte = endDate.toISOString().split('T')[0];
        }

        transformedFilters.periodType = calcPeriodType(filters.period);
        if (RESOURCES_REQUIRING_ANALYTICS_PERIOD_TYPE_NULLISH.includes(resource)) {
            transformedFilters['analytics.periodType_nullish'] = filters.period;
        }

        delete transformedFilters.period;
    }

    return transformedFilters;
};

const calcDateRange = (period, resource) => {
    const periodConfig = PERIOD_CONFIGS[period] ?? { days: period, type: PERIOD_TYPES.DEFAULT };
    const now = new Date();
    const isEndDateToday = RESOURCES_WITH_END_DATE_AS_TODAY.includes(getResourceBaseName(resource));
    const endDate = isEndDateToday ? new Date() : getYesterday();
    if (period === '0' && isEndDateToday) {
        return { startDate: new Date(START_OF_ANALYTICS_FOR_TRANSACTIONS), endDate };
    }
    if (periodConfig.start) {
        const startDate = new Date(periodConfig.start);
        return { startDate, endDate };
    }

    let startDate = new Date();

    if (periodConfig.days) {
        startDate.setDate(now.getDate() - periodConfig.days);
    } else if (periodConfig.months) {
        startDate.setMonth(now.getMonth() - periodConfig.months);
    }

    if (periodConfig.type === PERIOD_TYPES.WEEKLY) {
        const daysUntilMonday = (8 - startDate.getDay()) % 7;
        startDate.setDate(startDate.getDate() + daysUntilMonday);
    } else if (periodConfig.type === PERIOD_TYPES.MONTHLY) {
        if (1 !== startDate.getDate()) {
            startDate.setDate(1);
            startDate.setMonth(startDate.getMonth() + 1);
        }
    }

    return { startDate, endDate };
};

const dateWithoutTime = date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
